import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Application, ApplicationApi, ApplicationRequest } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';
import { UrlUtil } from '@venecia/utils';

@Component({
  selector: 'app-application-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './application-modal.html',
  styleUrl: './application-modal.scss',
})
export class ApplicationModal implements OnChanges {
  private readonly applicationApi = inject(ApplicationApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() application: Application | null = null;
  @Input() open = false;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly saving = signal(false);
  readonly submitted = signal(false);

  readonly selectedId = signal<number | null>(null);
  readonly key = signal<string | null>(null);
  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');
  readonly isIam = computed(() => this.application?.iam);

  readonly form = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(4)]],
    multiTenant: [false, [Validators.required]],
    logoUrl: ['', [Validators.maxLength(1024), UrlUtil.urlValidator()]],
    name: ['', [Validators.required, Validators.maxLength(256)]],
    siteUrl: ['', [Validators.required, Validators.maxLength(1024), UrlUtil.urlValidator()]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['application'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && this.saving()) {
      return;
    }

    this.closed.emit();
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      if (this.isCreateMode()) {
        await firstValueFrom(this.applicationApi.create(payload));
        this.messageService.success(await this.translate('pages.applications.messages.create-success'));
      } else {
        await firstValueFrom(this.applicationApi.update(this.selectedIdOrThrow(), payload));
        this.messageService.success(await this.translate('pages.applications.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.applications.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  isInvalid(controlName: keyof ApplicationFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  hasError(controlName: keyof ApplicationFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  private configureFromInputs(): void {
    this.submitted.set(false);

    const application = this.application;
    if (!application) {
      this.detailMode.set('create');
      this.selectedId.set(null);
      this.key.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedUpdatedAt.set(null);
      this.form.reset({
        code: '',
        multiTenant: false,
        logoUrl: '',
        name: '',
        siteUrl: '',
      });
      return;
    }

    this.detailMode.set('edit');
    this.selectedId.set(application.id);
    this.key.set(application.key ?? null);
    this.selectedCreatedAt.set(application['created-at'] ?? null);
    this.selectedUpdatedAt.set(application['updated-at'] ?? null);
    this.form.reset({
      code: application.code,
      multiTenant: application["multi-tenant"],
      logoUrl: application['logo-url'] ?? '',
      name: application.name,
      siteUrl: application['site-url'],
    });
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('Application id is required for update');
    }
    return id;
  }

  private toApiModel(): ApplicationRequest {
    const formValue = this.form.getRawValue();

    return {
      id: this.selectedId(),
      code: formValue.code.trim(),
      iam: this.application?.iam || false,
      "multi-tenant": formValue.multiTenant,
      'logo-url': formValue.logoUrl.trim() || undefined,
      name: formValue.name.trim(),
      'site-url': formValue.siteUrl.trim(),
    };
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface ApplicationFormModel {
  code: string;
  logoUrl: string;
  name: string;
  siteUrl: string;
}
